import { useState, useRef, useCallback, useMemo, type MutableRefObject } from "react";
import { CheckCircle, AlertCircle } from "lucide-react";
import { Type, type Props as TypeProps } from "@themes/pollinator/ui/Type";
import {
  useSendFriendInvitations,
  useFetchInvitationRecords,
  buildInvitationsSchemaWithLimit,
  calculateRemainingSlots,
  type InvitationsFormData,
} from "@core/features/invitation";
import {
  useStepFormSync,
  type StepValidationState,
} from "@platform/features/onboarding";
import { InvitationForm } from "./InvitationForm";

export type Props = {
  invitationId: number;
  onSuccess?: () => void;
  headingTypeVariant?: TypeProps["variant"];
  descriptionTypeVariant?: TypeProps["variant"];
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when step validation state changes (for parent footer) */
  onStepStateChange?: (state: StepValidationState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => Promise<void> | void) | null>;
  /** Custom heading text (default: "Invite Friends") */
  heading?: string;
  /** Custom description text */
  description?: string;
  /** Custom email placeholder */
  emailPlaceholder?: string;
  /** Maximum number of uses for this invitation (null = unlimited) */
  maxUses?: number | null;
  /** Whether to show the heading section (default: true) */
  showHeading?: boolean;
  /** Whether to show the usage hint (default: true) */
  showUsageHint?: boolean;
};

export function InvitationFormContainer({
  invitationId,
  onSuccess,
  headingTypeVariant = "h3",
  descriptionTypeVariant = "body2",
  showActionButtons = true,
  onStepStateChange,
  submitRef: externalSubmitRef,
  heading,
  description,
  emailPlaceholder,
  maxUses,
  showHeading = true,
  showUsageHint = true,
}: Props) {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formState, setFormState] = useState({ isDirty: false, isValid: false, isSubmitting: false });
  const internalSubmitRef = useRef<(() => void) | null>(null);

  // Fetch invitation records to calculate usage
  const { data: records = [] } = useFetchInvitationRecords({ invitationId });

  // Calculate used count from records (pending + claimed)
  const usedCount = useMemo(
    () =>
      records.filter(
        (r) => r.claimed_status === "pending" || r.claimed_status === "claimed"
      ).length,
    [records]
  );

  // Build dynamic schema with limit validation
  const schema = useMemo(
    () => buildInvitationsSchemaWithLimit({ maxUses: maxUses ?? null, usedCount }),
    [maxUses, usedCount]
  );

  // Calculate remaining slots for display
  const remainingSlots = useMemo(
    () => calculateRemainingSlots({ maxUses: maxUses ?? null, usedCount }),
    [maxUses, usedCount]
  );

  // Send friend invitations mutation
  const sendInvitationsMutation = useSendFriendInvitations({
    onSuccess: (data) => {
      if (data.sent_count > 0) {
        setSuccessMessage(
          `Successfully sent ${data.sent_count} invitation${
            data.sent_count > 1 ? "s" : ""
          }!`
        );
        onSuccess?.();
      }
      if (data.skipped.length > 0) {
        const skippedEmails = data.skipped
          .map((s) => `${s.email} (${s.reason})`)
          .join(", ");
        setErrorMessage(`Some invitations were skipped: ${skippedEmails}`);
      }
    },
    onError: (error) => {
      setErrorMessage("Failed to send invitations. Please try again.");
      console.error("Failed to send invitations:", error);
    },
  });

  const handleSubmit = async (data: InvitationsFormData) => {
    setSuccessMessage(null);
    setErrorMessage(null);

    // Filter out empty items and transform data
    const validInvitees = data.items
      .filter((item) => item.email.trim())
      .map((item) => ({
        email: item.email.trim(),
        firstName: item.firstName.trim(),
        lastName: item.lastName.trim(),
      }));

    if (validInvitees.length === 0) {
      setErrorMessage("Please enter at least one email address.");
      return;
    }

    // Send invitations with the invitation ID
    await sendInvitationsMutation.mutateAsync({
      invitationId,
      invitees: validInvitees,
    });
  };

  const isLoading = sendInvitationsMutation.isPending;

  // Handle form state changes
  const handleStateChange = useCallback((state: { isDirty: boolean; isValid: boolean; isSubmitting: boolean }) => {
    setFormState(state);
  }, []);

  // Wrap submit in useCallback for stable reference
  // If form is empty (not dirty), just complete the step without submitting
  const handleSubmitFromRef = useCallback(() => {
    if (!formState.isDirty) {
      // Form is empty, just complete the step (invitation is optional)
      onSuccess?.();
      return;
    }
    if (internalSubmitRef.current) {
      internalSubmitRef.current();
    }
  }, [formState.isDirty, onSuccess]);

  // Require form data for Continue — empty forms should use Skip instead
  const canProceed = formState.isDirty && formState.isValid && !formState.isSubmitting;

  // Sync form state with parent when action buttons are hidden
  useStepFormSync({
    shouldSync: !showActionButtons,
    onStepStateChange,
    submitRef: externalSubmitRef,
    formState: {
      isValid: formState.isValid,
      isDirty: formState.isDirty,
      isSubmitting: formState.isSubmitting || isLoading,
    },
    onSubmit: handleSubmitFromRef,
    canProceed,
    primaryLabel: formState.isDirty ? "Send Invitations" : undefined,
  });

  return (
    <div className="flex flex-col gap-6">
      {showHeading && (
        <div className="flex flex-col gap-1">
          <Type variant={headingTypeVariant} as="h2">
            {heading ?? "Invite Friends"}
          </Type>
          <Type variant={descriptionTypeVariant}>
            {description ??
              "Know someone who's aligned with Pollinator's ethos, but isn't yet eligible? We trust you to bring great people to our growing community of artists, technologists, and changemakers. Share their info below, and they'll receive an email invitation to join."}
          </Type>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-success-100 text-success-800">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <Type variant="body2">{successMessage}</Type>
        </div>
      )}

      {errorMessage && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-error-100 text-error-800">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <Type variant="body2">{errorMessage}</Type>
        </div>
      )}

      <InvitationForm
        isLoading={isLoading}
        onSubmit={handleSubmit}
        onCancel={() => {
          setSuccessMessage(null);
          setErrorMessage(null);
        }}
        showActionButtons={showActionButtons}
        onStateChange={handleStateChange}
        submitRef={internalSubmitRef}
        emailPlaceholder={emailPlaceholder}
        schema={schema}
        remainingSlots={remainingSlots}
        usedCount={usedCount}
        showUsageHint={showUsageHint}
      />
    </div>
  );
}

export default InvitationFormContainer;
